import { useEffect, useState } from 'preact/hooks';

/**
 * Detox mode: Курсы / Спот / Доход are viewable only on whitelisted
 * calendar days (full day, Moscow time). MSK = UTC+3, no DST.
 */

/** Allowed viewing days (calendar dates in Europe/Moscow) */
export const DETOX_ALLOWED_DATES: readonly string[] = [
  '2026-07-31',
  '2026-08-21',
  '2026-08-31',
  '2026-09-30',
  '2026-10-31',
  '2026-11-30',
  '2026-12-21',
];

const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;

/** Start of the given YYYY-MM-DD day in MSK, as UTC epoch ms */
function mskDayStart(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d) - MSK_OFFSET_MS;
}

/** Current calendar date in MSK as YYYY-MM-DD */
function mskToday(now: number): string {
  const shifted = new Date(now + MSK_OFFSET_MS);
  const y = shifted.getUTCFullYear();
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const d = String(shifted.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatDateRu(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

export interface DetoxState {
  /** true — сегодня разрешённый день, разделы открыты */
  open: boolean;
  /** будущие разрешённые даты (включая сегодняшнюю, если открыто) */
  upcoming: string[];
  /** уже наступившие даты расписания (прошедшие окна + сегодня, если открыто) */
  passed: string[];
  /** ms до открытия следующего окна (0 если открыто или дат больше нет) */
  msToOpen: number;
  /** ms до конца сегодняшнего открытого окна (0 если закрыто) */
  msToClose: number;
}

export function getDetoxState(now: number = Date.now()): DetoxState {
  const today = mskToday(now);
  const open = DETOX_ALLOWED_DATES.includes(today);
  const upcoming = DETOX_ALLOWED_DATES.filter((d) => d >= today);
  const passed = DETOX_ALLOWED_DATES.filter((d) => d < today || (open && d === today));
  const next = DETOX_ALLOWED_DATES.find((d) => d > today);
  return {
    open,
    upcoming,
    passed,
    msToOpen: open || !next ? 0 : Math.max(0, mskDayStart(next) - now),
    msToClose: open ? Math.max(0, mskDayStart(today) + 24 * 60 * 60 * 1000 - now) : 0,
  };
}

/** Reactive detox state, ticking once per second for the countdown */
export function useDetox(): DetoxState {
  const [state, setState] = useState<DetoxState>(() => getDetoxState());

  useEffect(() => {
    const id = setInterval(() => setState(getDetoxState()), 1000);
    return () => clearInterval(id);
  }, []);

  return state;
}

/** 12д 04:32:11 */
export function formatCountdown(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const h = String(Math.floor((totalSec % 86400) / 3600)).padStart(2, '0');
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
  const s = String(totalSec % 60).padStart(2, '0');
  return days > 0 ? `${days}д ${h}:${m}:${s}` : `${h}:${m}:${s}`;
}
